import React, { useState, useRef, useEffect } from "react";
import { Link } from "gatsby";
import { FaPlus, FaTimes } from "react-icons/fa";
import avatarSrc from "../../../images/memoji.png";
import GreetingBubble from "./GreetingBubble";
import ChatWindow from "./ChatWindow";
import { ShortcutItem } from "./types";
import { playNotificationSound } from "./utils";
import { getDefaultMenuItems } from "./constants";
import { useAppStore } from "../../../store/useAppStore";
import { getLocation } from "../../../util/location";

const ShortcutMenu: React.FC = () => {
  const {
    greetingOpen,
    openGreeting,
    closeGreeting,
    hasGreetingShown,
    markGreetingShown,
    dismissGreeting,
  } = useAppStore((state) => state);

  // State
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [soundPlayed, setSoundPlayed] = useState(false);
  const [countryName, setCountryName] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const played = sessionStorage.getItem("soundPlayed") === "true";
      setSoundPlayed(played);
    }
  }, []);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);

  // Get menu items
  const items = getDefaultMenuItems();

  // Detect mobile
  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 639px)").matches;


  // Greeting display logic
  useEffect(() => {
    if (hasGreetingShown) return;

    if (isMobile) {
      // Mobile: Show after 5-7s, then auto-hide after 10s with countdown
      let hideTimer: number;
      let countdownInterval: number;
      const showDelay = Math.random() * 2000 + 5000;

      const showTimer = window.setTimeout(() => {
        openGreeting(); // Use Zustand action instead of local state
        setCountdown(10);

        countdownInterval = window.setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) clearInterval(countdownInterval);
            return prev - 1;
          });
        }, 1000);

        hideTimer = window.setTimeout(() => {
          closeGreeting(); // Use Zustand action instead of local state
          clearInterval(countdownInterval);
          dismissGreeting();
        }, 10000);
      }, showDelay);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
        clearInterval(countdownInterval);
      };
    } else {
      // Desktop: show immediately
      openGreeting(); // Use Zustand action instead of local state
      markGreetingShown();
    }
  }, [isMobile, hasGreetingShown]);

  // Handle clicks outside menu/chat
  useEffect(() => {
    if (!open && !chatOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setChatOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, chatOpen]);

  // Play popup sound when greeting appears
  useEffect(() => {
    if (showGreeting && !soundPlayed) {
      playNotificationSound();
      sessionStorage.setItem("soundPlayed", "true");
    }
  }, [showGreeting, soundPlayed]);

  useEffect(() => {
    const fetchLocation = async () => {
      const locationData = await getLocation();

      if (locationData) {
        // Use both country name and country code for matching
        const countryName = locationData.country || "";
        const countryCode = locationData.countryCode || "";

        setCountryName(countryName);
        setCountryCode(countryCode);
      }
    };
    fetchLocation();
  }, []);

  // Handlers
  const handleChatOpen = () => {
    setChatOpen(true);
    setChatLoading(true);
    closeGreeting(); // Use Zustand action instead of local state
    setOpen(false);
    dismissGreeting();
  };

  const handleGreetingClose = () => {
    closeGreeting(); // Use Zustand action instead of local state
    dismissGreeting();
  };

  return (
    <>
      {/* Chat window embed */}
      {chatOpen && (
        <ChatWindow
          isLoading={chatLoading}
          onClose={() => setChatOpen(false)}
          isMobile={isMobile}
        />
      )}
      <div
        ref={containerRef}
        className="fixed bottom-20 right-4 z-50 flex flex-col items-end space-y-2"
      >
        {/* Greeting bubble */}
        {(countryName || countryCode) &&
          greetingOpen &&
          !chatOpen && ( // Use Zustand state instead of local state
            <GreetingBubble
              onClose={handleGreetingClose}
              onChatOpen={handleChatOpen}
              countdown={countdown}
              isMobile={isMobile}
              countryName={countryName ?? ""}
              countryCode={countryCode ?? ""}
            />
          )}

        {/* Dedicated chat toggle */}
        <button
          aria-label={chatOpen ? "Close chat" : "Open chat"}
          onClick={() => {
            if (!chatOpen) setChatLoading(true);
            setShowGreeting(false);
            setChatOpen((prev) => !prev);
            setOpen(false);
          }}
          className="rounded-full bg-primary p-4 text-white shadow-[0px_4px_8px_rgba(0,0,0,0.25)] transition hover:bg-secondary"
        >
          <img
            src={avatarSrc}
            alt="Poppy Avatar"
            className="h-6 w-6 rounded-full"
          />
        </button>

        {/* Shortcut items */}
        {open && (
          <>
            {items.map((item) => {
              // Action button
              if (item.action) {
                return (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="flex items-center rounded-lg bg-white bg-opacity-20 p-3 text-primary shadow-[0px_4px_8px_rgba(0,0,0,0.25)] backdrop-blur-sm transition hover:bg-green-100"
                  >
                    {item.icon}
                    <span className="ml-2 font-medium">{item.label}</span>
                  </button>
                );
              }

              // External link
              if (item.external) {
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center rounded-lg bg-white bg-opacity-20 p-3 text-primary shadow-[0px_4px_8px_rgba(0,0,0,0.25)] backdrop-blur-sm transition hover:bg-green-100"
                  >
                    {item.icon}
                    <span className="ml-2 font-medium">{item.label}</span>
                  </a>
                );
              }

              // Internal Gatsby link
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className="flex items-center rounded-lg bg-white bg-opacity-20 p-3 text-primary shadow-[0px_4px_8px_rgba(0,0,0,0.25)] backdrop-blur-sm transition hover:bg-green-100"
                  onClick={() => setOpen(false)}
                >
                  {item.icon}
                  <span className="ml-2 font-medium">{item.label}</span>
                </Link>
              );
            })}
          </>
        )}

        {/* Toggle button */}
        <button
          onClick={() => setOpen((prev) => !prev)}
          className="rounded-full bg-primary p-4 text-white shadow-[0px_4px_8px_rgba(0,0,0,0.25)] transition hover:bg-secondary"
        >
          {open ? (
            <FaTimes className="text-xl" />
          ) : (
            <FaPlus className="text-xl" />
          )}
        </button>
      </div>
    </>
  );
};

export default ShortcutMenu;
